import React, { useEffect } from "react";
import { Card, CardBody, Row, Col, Input, Label, FormGroup } from "reactstrap";
import { StepComponentProps } from "../types/proposalForm.types";

const Step2PersonalInfo: React.FC<StepComponentProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  // Determine country code based on environment
  const isProduction = process.env.NODE_ENV === 'production';
  const countryCode = isProduction ? '+973' : '+91';
  const flagEmoji = isProduction ? '🇧🇭' : '🇮🇳';

  // Set default country codes on mount
  useEffect(() => {
    if (!values.local_country_code) {
      setFieldValue('local_country_code', countryCode);
    }
    if (!values.home_country_code) {
      setFieldValue('home_country_code', countryCode);
    }
  }, [countryCode, setFieldValue, values.local_country_code, values.home_country_code]);
  const renderInput = (
    name: keyof typeof values,
    label: string,
    type: "text" | "number" | "date" | "email" | "tel" = "text",
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type={type}
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderSelect = (
    name: keyof typeof values,
    label: string,
    options: Array<{ value: string; label: string }>,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="select"
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        className="form-control"
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderTextarea = (
    name: keyof typeof values,
    label: string,
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="textarea"
        name={name as string}
        value={String(values[name] || "")}
        onChange={(e) => {
          handleChange(e);
          if (name === "address" && values.same_as_communication_address) {
            setFieldValue("permanent_address", e.target.value);
          }
        }}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
        rows={3}
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderDateInput = (
    name: keyof typeof values,
    label: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <div className="date-input-wrapper">
        <Input
          type="date"
          name={name as string}
          value={String(values[name] || "")}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={!!(touched[name] && errors[name])}
          className="form-control"
          id={`date-${name}`}
        />
        <i 
          className="ri-calendar-line calendar-icon"
          onClick={() => {
            const input = document.getElementById(`date-${name}`) as HTMLInputElement;
            if (input) {
              if (input.showPicker) {
                input.showPicker();
              } else {
                input.click();
              }
            }
          }}
        />
      </div>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderCountryCodeSelect = (
    name: keyof typeof values,
    label: string
  ) => {
    const currentCode = values[name] || countryCode;
    
    return (
      <FormGroup className="form-group">
        <Label>{label}</Label>
        <div className="country-code-wrapper">
          <Input
            type="text"
            name={name as string}
            value={String(currentCode)}
            readOnly={true}
            className="form-control country-code-display"
            style={{ maxWidth: '100px', width: '100%' }}
          />
          {/* Hidden input to store the actual country code value */}
          <input
            type="hidden"
            name={name as string}
            value={String(currentCode)}
          />
        </div>
      </FormGroup>
    );
  };

  return (
    <Card className="form-card shadow-sm border-0">
      <CardBody>
        <h5 className="form-title">Personal Details</h5>
        <p className="form-subtitle">
          Life to be assured - Please provide your personal information.
        </p>

        <Row>
          {/* Row 1 */}
          <Col md={4}>
            {renderInput("first_name", "First Name", "text", "XXX", true)}
          </Col>
          <Col md={4}>
            {renderInput("middle_name", "Middle Name", "text", "XXX")}
          </Col>
          <Col md={4}>
            {renderInput("last_name", "Last Name", "text", "XXX", true)}
          </Col>

          {/* Row 2 */}
          <Col md={6}>
            {renderInput("father_full_name", "Father's Full Name", "text", "Type here")}
          </Col>
          <Col md={6}>
            {renderInput(
              "nationality",
              "Nationality (mention names of countries of which you are holding citizenship)",
              "text",
              "XXX",
              true
            )}
          </Col>

          {/* Row 3 */}
          <Col md={4}>
            {renderInput(
              "country_of_birth",
              "Country of Birth",
              "text",
              undefined,
              true
            )}
          </Col>
          <Col md={4}>
            {renderInput(
              "place_of_birth",
              "Place of Birth",
              "text",
              undefined,
              true
            )}
          </Col>
          <Col md={4}>{renderDateInput("date_of_birth", "Date of Birth", true)}</Col>
        </Row>

        {/* Gender Section */}
        <div className="gender-section">
          <Label className="gender-label">
            Gender<span className="text-danger">*</span>
          </Label>
          <div className="gender-options">
            {(["Male", "Female", "Others"] as const).map((gender) => (
              <div key={gender} className="gender-option">
                <input
                  type="radio"
                  id={`gender-${gender}`}
                  name="gender"
                  value={gender}
                  checked={values.gender === gender}
                  onChange={(e) => setFieldValue("gender", e.target.value)}
                  className="gender-radio"
                />
                <label htmlFor={`gender-${gender}`} className="gender-label-option">
                  {gender}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Address Sections */}
        <Row>
          <Col md={12}>
            {renderTextarea(
              "address",
              "Address for Communication",
              "Type here",
              true
            )}
          </Col>
          <Col md={12}>
            {renderTextarea(
              "permanent_address",
              "Permanent Residential Address",
              "Type here"
            )}
          </Col>
        </Row>

        {/* Same as Communication Address Checkbox */}
        <div className="checkbox-section">
          <FormGroup check className="form-check">
            <Input
              type="checkbox"
              id="same_as_communication_address"
              name="same_as_communication_address"
              checked={values.same_as_communication_address || false}
              onChange={(e) => {
                const checked = e.target.checked;
                setFieldValue("same_as_communication_address", checked);
                if (checked) setFieldValue("permanent_address", values.address || "");
              }}
              className="form-check-input"
            />
            <Label check htmlFor="same_as_communication_address" className="form-check-label">
              Same as Communication Address
            </Label>
          </FormGroup>
        </div>

        {/* Contact Details at Present Place of Residence */}
        <div className="contact-section">
          <h6 className="contact-section-title">
            Contact Details at Present Place of Residence
            <span className="text-danger">*</span>
          </h6>
          <Row className="align-items-end">
            <Col md="auto" style={{ maxWidth: '120px' }}>
              {renderCountryCodeSelect("local_country_code", "Country code")}
            </Col>
            <Col md={3}>
              {renderInput("local_mobile_number", "Mobile number", "tel", "123")}
            </Col>
            <Col md={3}>
              {renderInput("local_office_number", "Office", "tel", "123")}
            </Col>
            <Col md>
              {renderInput("local_residence_number", "Residence", "tel", "XXX")}
            </Col>
          </Row>
        </div>

        {/* Contact Numbers in Home Country */}
        <div className="contact-section">
          <h6 className="contact-section-title">Contact Numbers in Home Country</h6>
          <Row className="align-items-end">
            <Col md="auto" style={{ maxWidth: '120px' }}>
              {renderCountryCodeSelect("home_country_code", "Country code")}
            </Col>
            <Col md={3}>
              {renderInput("home_mobile_number", "Mobile number", "tel", "123")}
            </Col>
            <Col md={3}>
              {renderInput("home_office_number", "Office", "tel", "123")}
            </Col>
            <Col md>
              {renderInput("home_residence_number", "Residence", "tel", "XXX")}
            </Col>
          </Row>
        </div>

        {/* Email ID */}
        <Row>
          <Col md={12}>
            {renderInput("homeemail", "Email ID", "email", "Type here")}
          </Col>
        </Row>

        {/* Note Section */}
        <div className="note-section">
          <p className="note-text">
            <strong>Note:</strong> Please provide all email IDs currently used for official
            communication.
          </p>
        </div>
      </CardBody>
    </Card>
  );
};

export default Step2PersonalInfo;
